import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';

const HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));
const BASE_MINUTES = Array.from({ length: 12 }, (_, index) => String(index * 5).padStart(2, '0'));
const PERIODS = ['AM', 'PM'];
// Matches combineDueAt's fallback when no time is chosen, so the seeded draft
// equals what the backend would store anyway.
const DEFAULT_DRAFT = { hour: '09', minute: '00', period: 'AM' };

/** Parse a 24-hour "HH:MM" value into { hour, minute, period } (12-hour parts). */
export function parseTimeValue(value) {
  if (!value || typeof value !== 'string') return null;
  const match = /^(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours24 = Number(match[1]);
  const minute = match[2];
  if (hours24 > 23 || Number(minute) > 59) return null;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return { hour: String(hours12).padStart(2, '0'), minute, period };
}

/** Convert 12-hour parts back to the stored 24-hour "HH:MM" form. */
export function to24HourValue({ hour, minute, period }) {
  let hours24 = Number(hour);
  if (period === 'AM' && hours24 === 12) hours24 = 0;
  if (period === 'PM' && hours24 !== 12) hours24 += 12;
  return `${String(hours24).padStart(2, '0')}:${minute}`;
}

/** Display form, e.g. "13:00" -> "01:00 PM". Empty string when unset. */
export function formatTimeDisplay(value) {
  const parsed = parseTimeValue(value);
  if (!parsed) return '';
  return `${parsed.hour}:${parsed.minute} ${parsed.period}`;
}

function TimeColumn({ title, options, selected, onSelect, columnRef }) {
  return (
    <div className="min-w-0">
      <p className="mb-2 text-center text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      <div
        ref={columnRef}
        className="grid max-h-56 gap-1 overflow-y-auto scroll-smooth p-0.5 [overscroll-behavior:contain] [scrollbar-width:thin]"
      >
        {options.map((option) => {
          const isSelected = option === selected;
          return (
            <button
              key={option}
              type="button"
              data-selected={isSelected || undefined}
              aria-pressed={isSelected}
              className={cn(
                'h-10 w-full shrink-0 cursor-pointer rounded-lg px-2 text-sm font-semibold transition-colors',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                isSelected
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-foreground hover:bg-primary/10 hover:text-primary',
              )}
              onClick={() => onSelect(option)}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Custom Due-time field: the whole field is a single button that opens an
 * Hour / Minute / AM-PM dropdown. Displays 12-hour time; emits 24-hour "HH:MM"
 * through onChange, so the task save payload is unchanged.
 */
export function TaskTimePicker({
  value,
  onChange,
  placeholder = 'Select time',
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => parseTimeValue(value) || DEFAULT_DRAFT);
  const hourColumnRef = useRef(null);
  const minuteColumnRef = useRef(null);
  const display = formatTimeDisplay(value);

  const minutes = useMemo(() => {
    if (draft.minute && !BASE_MINUTES.includes(draft.minute)) {
      return [...BASE_MINUTES, draft.minute].sort();
    }
    return BASE_MINUTES;
  }, [draft.minute]);

  useEffect(() => {
    if (!open) return;
    // Center the current selections once the dropdown has rendered.
    requestAnimationFrame(() => {
      [hourColumnRef, minuteColumnRef].forEach((ref) => {
        ref.current
          ?.querySelector('[data-selected]')
          ?.scrollIntoView({ block: 'center' });
      });
    });
  }, [open]);

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      setDraft(parseTimeValue(value) || DEFAULT_DRAFT);
    }
    setOpen(nextOpen);
  };

  const applyDraft = () => {
    onChange?.(to24HourValue(draft));
    setOpen(false);
  };

  const clearValue = () => {
    onChange?.('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={ariaLabel || 'Due time'}
          data-testid={dataTestId}
          className={cn(
            'flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-md border px-3 py-1 text-left text-sm shadow-sm transition-colors',
            'border-slate-300 bg-white text-slate-900 hover:border-slate-400 hover:bg-slate-50',
            'dark:border-[rgba(142,218,182,0.20)] dark:bg-[rgba(10,38,28,0.50)] dark:text-[#EAF7F0] dark:hover:border-emerald-400/40 dark:hover:bg-[rgba(10,38,28,0.70)]',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:border-teal-500 dark:focus-visible:ring-emerald-400 dark:focus-visible:border-emerald-400',
            className,
          )}
        >
          <span
            className={cn(
              'truncate font-medium',
              !display && 'font-normal text-slate-400 dark:text-[#93AE9F]',
            )}
          >
            {display || placeholder}
          </span>
          {/* Decorative only — clicks pass through to the field button. */}
          <Clock
            aria-hidden="true"
            className="pointer-events-none h-4 w-4 shrink-0 text-muted-foreground"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={12}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className={cn(
          'z-[80] rounded-2xl border-border bg-popover p-3 text-popover-foreground shadow-xl',
          'w-[var(--radix-popover-trigger-width)] min-w-[17rem] max-w-[min(22.5rem,calc(100vw-1.5rem))]',
        )}
        role="dialog"
        aria-label="Select due time"
        data-testid="task-due-time-picker"
      >
        <div className="grid grid-cols-[1fr_1fr_0.9fr] gap-2">
          <TimeColumn
            title="Hour"
            options={HOURS}
            selected={draft.hour}
            onSelect={(hour) => setDraft((prev) => ({ ...prev, hour }))}
            columnRef={hourColumnRef}
          />
          <TimeColumn
            title="Minute"
            options={minutes}
            selected={draft.minute}
            onSelect={(minute) => setDraft((prev) => ({ ...prev, minute }))}
            columnRef={minuteColumnRef}
          />
          <TimeColumn
            title="Period"
            options={PERIODS}
            selected={draft.period}
            onSelect={(period) => setDraft((prev) => ({ ...prev, period }))}
          />
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearValue}
            data-testid="task-due-time-clear"
          >
            Clear
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              data-testid="task-due-time-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={applyDraft}
              data-testid="task-due-time-apply"
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default TaskTimePicker;
